import { FC, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ImageGallery } from './components/ImageGallery'
import { RecolorModal } from './components/RecolorModal'
import { CombinedImageItem, AnalyzeRecolorResponse } from './types'
import { useProductImageRecolor } from './hooks/useProductImageRecolor'

/**
 * Tela 2: Galeria de Imagens + Recolor
 * Padrão: Food SmartOrder (detalhe)
 * Rota: /product-image-recolor/:productId
 */
const ProductImageRecolorDetailPage: FC = () => {
  const { productId } = useParams<{ productId: string }>()
  const navigate = useNavigate()
  const id = productId ? parseInt(productId, 10) : 0

  const {
    loadProductImages,
    loadProductVariations,
    combineProductImages,
    analyzeRecolor,
    applyRecolorTransformation,
    saveRecolor,
    loading,
    error,
  } = useProductImageRecolor()

  // ========================================================================
  // STATE
  // ========================================================================
  const [images, setImages] = useState<CombinedImageItem[]>([])
  const [selectedImage, setSelectedImage] = useState<CombinedImageItem | null>(null)
  const [recolorModalVisible, setRecolorModalVisible] = useState(false)
  const [analysis, setAnalysis] = useState<AnalyzeRecolorResponse['data'] | null>(null)
  const [recolorLoading, setRecolorLoading] = useState(false)
  const [recolorError, setRecolorError] = useState<string | null>(null)

  // ========================================================================
  // LOAD INICIAL (useEffect)
  // ========================================================================
  useEffect(() => {
    if (id > 0) {
      loadImagesData()
    }
  }, [id])

  const loadImagesData = async () => {
    try {
      // ✅ CARREGA IMAGENS DE PRODUTO + VARIAÇÕES
      const [productImgs, variations] = await Promise.all([
        loadProductImages(id),
        loadProductVariations(id),
      ])

      // ✅ COMBINA TUDO
      const combined = combineProductImages(id, productImgs, variations)
      setImages(combined)

      if (combined.length > 0) {
        setSelectedImage(combined[0]) // Seleciona primeira imagem
      }
    } catch (err) {
      console.error('Erro ao carregar imagens:', err)
    }
  }

  // ========================================================================
  // FUNÇÕES DE RECOLOR
  // ========================================================================
  const handleAnalyzeRecolor = async (
    imageUrl: string,
    color: string,
    description: string
  ) => {
    try {
      setRecolorLoading(true)
      setRecolorError(null)

      const result = await analyzeRecolor({
        imageUrl,
        objectDescription: description,
        targetColor: color,
      })

      if (result) {
        setAnalysis(result)
      } else {
        throw new Error('Erro ao analisar recolor')
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao analisar'
      setRecolorError(errorMsg)
      throw err
    } finally {
      setRecolorLoading(false)
    }
  }

  const handleSaveRecolor = async (imageBase64: string, color: string): Promise<string | null> => {
    try {
      setRecolorLoading(true)
      setRecolorError(null)

      // ✅ imageBase64 já foi transformado pelo Jimp no modal
      const result = await saveRecolor({
        imageBase64,
        subdomain: 'produtos',
        typefolder: 'recolor',
        typeimage: 'recolor-' + Date.now(),
      })

      if (result && result.url) {
        return result.url
      }

      throw new Error('Erro ao salvar recolor')
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao salvar'
      setRecolorError(errorMsg)
      throw err
    } finally {
      setRecolorLoading(false)
    }
  }

  const handleOpenRecolorModal = () => {
    if (selectedImage) {
      setAnalysis(null) // Reset análise anterior
      setRecolorModalVisible(true)
    }
  }

  // ========================================================================
  // RENDER
  // ========================================================================
  return (
    <div className='page'>
      {/* BREADCRUMB */}
      <div className='page-header d-print-none'>
        <div className='container-xl'>
          <div className='row align-items-center'>
            <div className='col'>
              <h2 className='page-title'>
                <i className='bi bi-image me-2'></i>
                Imagens do Produto #{id}
              </h2>
              <div className='page-pretitle'>
                Selecione uma imagem e aplique recolorização
              </div>
            </div>
            <div className='col-auto'>
              <button
                className='btn btn-secondary'
                onClick={() => navigate('/product-image-recolor')}
              >
                <i className='bi bi-arrow-left me-2'></i>
                Voltar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CONTEÚDO */}
      <div className='page-wrapper'>
        <div className='container-xl'>
          {error && (
            <div className='row mb-3'>
              <div className='col-12'>
                <div className='alert alert-danger' role='alert'>
                  <i className='bi bi-exclamation-triangle me-2'></i>
                  {error}
                </div>
              </div>
            </div>
          )}

          <div className='row'>
            {/* GALERIA */}
            <div className='col-12 mb-4'>
              <div className='card card-bordered'>
                <div className='card-header'>
                  <h5 className='card-title'>
                    <i className='bi bi-images me-2'></i>
                    Galeria de Imagens
                  </h5>
                </div>
                <div className='card-body'>
                  <ImageGallery
                    images={images}
                    selectedImage={selectedImage}
                    onSelectImage={setSelectedImage}
                    loading={loading}
                  />
                </div>
              </div>
            </div>

            {/* PREVIEW + AÇÕES */}
            {selectedImage && (
              <div className='col-12 col-md-6 mb-4'>
                <div className='card card-bordered'>
                  <div className='card-header'>
                    <h5 className='card-title'>Preview</h5>
                  </div>
                  <div className='card-body text-center'>
                    <img
                      src={selectedImage.imageUrl}
                      alt={selectedImage.label}
                      className='img-fluid rounded mb-3'
                      style={{ maxHeight: '300px', objectFit: 'contain' }}
                      onError={(e) => {
                        ;(e.target as HTMLImageElement).src = 'https://placehold.co/300x300?text=Erro'
                      }}
                    />
                    <p className='text-muted small mb-0'>{selectedImage.label}</p>
                  </div>
                </div>
              </div>
            )}

            {/* AÇÕES */}
            <div className='col-12 col-md-6 mb-4'>
              <div className='card card-bordered'>
                <div className='card-header'>
                  <h5 className='card-title'>Ações</h5>
                </div>
                <div className='card-body'>
                  <button
                    className='btn btn-primary w-100 mb-2'
                    onClick={handleOpenRecolorModal}
                    disabled={!selectedImage || loading}
                  >
                    <i className='bi bi-palette me-2'></i>
                    Recolorizar Imagem
                  </button>

                  <button
                    className='btn btn-outline-secondary w-100'
                    onClick={loadImagesData}
                    disabled={loading}
                  >
                    <i className='bi bi-arrow-clockwise me-2'></i>
                    Atualizar Galeria
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL RECOLOR */}
      <RecolorModal
        visible={recolorModalVisible}
        image={selectedImage}
        onClose={() => setRecolorModalVisible(false)}
        onAnalyze={handleAnalyzeRecolor}
        onSave={handleSaveRecolor}
        onApplyTransformation={applyRecolorTransformation}
        analysis={analysis}
        loading={recolorLoading}
        error={recolorError}
      />
    </div>
  )
}

export default ProductImageRecolorDetailPage
